<script setup lang="ts">
import { TeamSide } from '@collector/shared-domain'
import { Button } from '@collector/shared-ui'
import { getEventTime } from '@collector/shared-ui-domain'
import { Incident } from '@collector/sportsapi-client'
import { Confirmation } from '@collector/sportsapi-client-legacy'
import SportIncidentIcon from '@desktop/views/Relation/components/RelationView/SportIncidentIcon.vue'
import { useEventIncidentsQueue } from '@desktop/views/Relation/Sports/composables'

interface Props {
  incident: Incident
  teamSide: TeamSide
}

const props = defineProps<Props>()

// composables
const eventIncidentsQueue = useEventIncidentsQueue()

// methods
function confirmIncident(confirmation: Confirmation): void {
  eventIncidentsQueue.updateIncident({ confirmation }, props.incident)
}
</script>

<template>
  <div
    class="flex justify-between rounded-lg border p-0.75"
    :class="{
      'border-current-team-home-5': teamSide === 'home',
      'border-current-team-away-5': teamSide === 'away',
      [`notify-pulse-team-${teamSide}`]: !!incident,
    }"
  >
    <Button
      class="bg-error-5 border-error-5 hover:bg-error-7 hover:border-error-7 text-neutral-10"
      icon="xmark"
      size="xs"
      square
      aria-label="cancel"
      @click="confirmIncident(Confirmation.Cancelled)"
    />
    <span
      class="flex items-center"
      data-testid="confirm-incidents-element"
    >
      <slot>
        <SportIncidentIcon
          :incidentId="incident.incident_id"
          size="3xs"
        />
        <span class="w-12 text-center text-xs font-bold select-none">
          {{ getEventTime(incident) }}
        </span>
      </slot>
    </span>
    <Button
      class="bg-success-2 border-success-2 hover:border-success-4 hover:bg-success-4 text-neutral-10"
      size="xs"
      icon="check"
      square
      aria-label="confirm"
      @click="confirmIncident(Confirmation.Confirmed)"
    />
  </div>
</template>

<style scoped lang="scss">
$duration: 1.5s;

.notify-pulse-team {
  &-home {
    animation: notify-pulse-current-team-home-animation $duration infinite;
  }

  @keyframes notify-pulse-current-team-home-animation {
    50% {
      background: rgba(var(--current-home-color-5), 0.3);
    }
  }

  &-away {
    animation: notify-pulse-current-team-away-animation $duration infinite;
  }
  @keyframes notify-pulse-current-team-away-animation {
    50% {
      background: rgba(var(--current-away-color-5), 0.3);
    }
  }
}
</style>
